import React, { ReactNode, useEffect, useRef, useState } from 'react';
import { useRouter } from 'next/router';
import { useTranslation } from 'react-i18next';
import { PhotosGridItem } from '../PhotosGridItem';
import { urlHashes } from '../../../../utils/helpers/constants';
import {
  getModalStyles,
  PhotosGridWrapper,
  ShowAllBtn,
  CurrentItemTitleWrapper,
  modalBackdropStyles
} from './styled';
import { Photo } from '../../../../graphql/types';
import { Typography } from '../../../ui/Typography';
import { Modal } from '../../../ui/Modal';
import { CurrentItemTitle } from '../../Sections/CurrentItemTitle';

interface PhotosGridProps {
  photos?: Photo[];
  description: ReactNode;
  pageNumber: number;
  isAuthor: boolean;
  isPublished: boolean;
  itemId: string;
  isReported: boolean;
  isFavorited: boolean;
  setFavorited: React.Dispatch<React.SetStateAction<boolean>>;
  onOpenGallery: (
    e: React.MouseEvent<HTMLDivElement, MouseEvent>,
    photoNumber?: number
  ) => void;
  setIsAdReported: React.Dispatch<React.SetStateAction<boolean>>;
}

export const PhotosGrid: React.FC<PhotosGridProps> = ({
  photos,
  description,
  pageNumber,
  isAuthor,
  isPublished,
  itemId,
  isReported,
  isFavorited,
  setFavorited,
  onOpenGallery,
  setIsAdReported
}) => {
  const { t } = useTranslation();
  const router = useRouter();

  const [isGoFromGrid, setIsGoFromGrid] = useState(false);
  const [isOpenGrid, setIsOpenGrid] = useState(false);

  const gridWrapperRef = useRef<HTMLDivElement | null>(null);
  const pageScrollYRef = useRef<number>(0);

  const openModal = () => {
    pageScrollYRef.current = window.scrollY || window.pageYOffset || 0;
    setIsOpenGrid(true);

    const parsedUrl = new URL(router.asPath, window.location);

    router.push({
      pathname: parsedUrl.pathname,
      hash: urlHashes.photosGrid,
      search: parsedUrl.search
    });
  };

  const closeModal = () => {
    setIsOpenGrid(false);
    router.replace(router.asPath.split('#')[0], undefined, {
      shallow: true,
      scroll: false
    });
  };

  useEffect(() => {
    const parsedUrl = new URL(router.asPath, window.location);
    const isGridOpen =
      parsedUrl.hash === `#${urlHashes.photosGrid}` ||
      (parsedUrl.hash === `#${urlHashes.fullScreenGallery}` && isGoFromGrid);

    setIsOpenGrid(isGridOpen);
  }, [router.asPath]);

  const handleOpenGallery = (
    e: React.MouseEvent<HTMLDivElement, MouseEvent>,
    photoNumber?: number
  ) => {
    sessionStorage.setItem('cameFromGrid', 'true');
    onOpenGallery(e, photoNumber);
  };

  useEffect(() => {
    if (!isOpenGrid) {
      setIsGoFromGrid(false);
    }
  }, [isOpenGrid]);

  const modalStyles = getModalStyles(isOpenGrid);

  return (
    <>
      <ShowAllBtn
        onClick={openModal}
        data-cy="show-all-photos"
        data-stat="show-all-photos"
        role="button"
        tabIndex={0}
      >
        <Typography tag="caption_1_new">
          {t('pages.current_item.buttons.all_photos')}
        </Typography>
      </ShowAllBtn>

      <Modal
        isOpen={isOpenGrid}
        modalId="all-photos"
        modalStyles={modalStyles}
        modalBackdropStyles={modalBackdropStyles}
        withAnimation={false}
        header={
          <CurrentItemTitleWrapper>
            <CurrentItemTitle
              title={description}
              isReportButtonShown={!isAuthor}
              isBookmarkedButtonShow={isPublished}
              itemId={itemId}
              isReported={isReported}
              isFavorited={isFavorited}
              setFavorited={setFavorited}
              isPhotosGrid
              handleBack={closeModal}
              setIsAdReported={setIsAdReported}
            />
          </CurrentItemTitleWrapper>
        }
      >
        <PhotosGridWrapper
          ref={gridWrapperRef}
          onTouchStart={() => setIsGoFromGrid(true)}
          $isLessThan3={(photos?.length || 0) < 3}
          data-cy="photos-grid"
        >
          {photos?.map((photo, index, arr) => {
            return (
              <PhotosGridItem
                key={photo.full}
                photo={photo}
                index={index}
                photosCount={arr.length}
                pageNumber={pageNumber}
                onOpenGallery={handleOpenGallery}
              />
            );
          })}
        </PhotosGridWrapper>
      </Modal>
    </>
  );
};
